import Vue from 'vue';
import VueRouter from 'vue-router';
import PageAddressInvalid from '~/components/address/PageAddressInvalid.vue';
import PageAddress from '~/components/address/PageAddress.vue';
import PageBlock from '~/components/block/PageBlock.vue';
import PageBlockLatest from '~/components/block/PageBlockLatest.vue';
import PageBlocks from '~/components/block/PageBlocks.vue';
import PageIndex from '~/components/PageIndex.vue';
import PageTx from '~/components/tx/PageTx.vue';
import PageNft from '~/components/nft/PageNft.vue';
import PageNftCollection from '~/components/nft/PageCollection.vue';
import PageJetton from '~/components/jetton/PageJetton.vue';
import PagePool from '~/components/pool/Pool.vue';
import PageStats from '~/components/stats/PageStats.vue';
import PageVesting from '~/components/pool/PageVesting.vue';
import PageMultisig from '~/components/multisig/PageMultisig.vue';
import PageMultisigOrder from '~/components/multisig/PageMultisigOrder.vue';
import PageSingleNominator from '~/components/pool/PageSingleNominator.vue';
import PageLocker from '~/components/address/PageLocker.vue';
import PageValidators from '~/components/validators/PageValidators.vue';
import PageSuspended from '~/components/address/PageSuspended.vue';
import PageMarket from '~/components/market/PageMarket.vue';
import PageWhales from '~/components/PageWhales.vue';
import PageError from '~/components/PageError.vue';
import PageConfig from '~/components/config/PageConfig.vue';
import { ADDRESS_REGEX, APP_MAIN_LOCALE, TELEGRAM_USERNAME_REGEX, TON_DOMAIN_REGEX } from '~/config.js';
import PageApps from '~/components/apps/PageApps.vue';
import AppWrapper from '~/components/apps/App/AppWrapper.vue';
import PageJettonWallet from '~/components/jettonWallet/PageJetton.vue';
import PageAdvertise from '~/components/advertisement/PageAdvertise.vue';
import { getAddressByDomain } from '~/api/toncenterV3';

Vue.use(VueRouter);

const router = new VueRouter({
    hashbang: false,
    mode: 'history',
    /* eslint consistent-return: "off" */
    scrollBehavior(to, from, savedPosition) {
        // Exception for holders hash change event on jetton page
        if (to.name === 'jetton' && to.hash === '#holders') return;
        if (to.name === 'apps' && from.name === 'apps') {
            return savedPosition;
        }
        return { x: 0, y: 0 };
    },
    /* eslint no-useless-escape: "off" */
    routes: [{
        path: '/:lang(ru|en)?',
        component: {
            render: h => h('router-view'),
        },
        children: [{
            name: 'index',
            path: '/',
            component: PageIndex,
        }, {
            name: 'address',
            path: `address/:address(${ADDRESS_REGEX.source})`,
            component: PageAddress,
            props: true,
        }, {
            name: 'address_invalid',
            path: 'address/:address',
            component: PageAddressInvalid,
            props: true,
        }, {
            // redirect from tonapi format:
            path: `account/:address(${ADDRESS_REGEX.source})`,
            redirect: { name: 'address' },
        }, {
            name: 'tx_by_msg_hash',
            path: 'tx/by-msg-hash/:hash([^:]+)',
            component: PageTx,
            props: true,
        }, {
            path: 'transaction/:hash([^:$]+)',
            redirect: { name: 'tx' },
        }, {
            // redirect from OKX vsraty format:
            path: 'tx/&lt=:lt([\\d]+)&hash=:hash([^:$]+)',
            redirect: { name: 'tx' },
        }, {
            // redirect from old transaction format (lt:hash:address):
            path: 'tx/:lt([\\d]+)\::hash([^:]+)\::address(.{48})',
            redirect: { name: 'tx' },
        }, {
            // redirect from ancient transaction format (lt$hash$address):
            path: 'tx/:lt([\\d]+)$:hash([^:$]+)$:address(.{48})',
            redirect: { name: 'tx' },
        }, {
            name: 'tx',
            path: 'tx/:hash([^:$]{44,})',
            component: PageTx,
            props: true,
        }, {
            name: 'block',
            path: 'block/:workchain([\\-\\d]+)\::shard([a-fA-F\\d]+)\::seqno([\\d]+)',
            component: PageBlock,
            props: true,
        }, {
            name: 'block_latest',
            path: 'block/latest',
            component: PageBlockLatest,
        }, {
            name: 'blocks',
            path: 'blocks',
            component: PageBlocks,
        }, {
            name: 'nft',
            path: `nft/:address(${ADDRESS_REGEX.source})`,
            component: PageNft,
            props: true,
        }, {
            path: 'nft/:address',
            redirect: { name: 'address' },
        }, {
            name: 'collection',
            path: `collection/:address(${ADDRESS_REGEX.source})`,
            component: PageNftCollection,
            props: true,
        }, {
            name: 'nft_item',
            path: `nft_item/:address(${ADDRESS_REGEX.source})`,
            redirect: { name: 'nft', replace: true },
        }, {
            name: 'jetton',
            path: `jetton/:address(${ADDRESS_REGEX.source})`,
            component: PageJetton,
            props: true,
        }, {
            path: 'jetton/:address',
            redirect: { name: 'address' },
        }, {
            name: 'jetton-wallet',
            path: `jetton-wallet/:address(${ADDRESS_REGEX.source})`,
            component: PageJettonWallet,
            props: true,
        }, {
            path: `nominator/:address(${ADDRESS_REGEX.source})`,
            redirect: { name: 'nominator' },
        }, {
            name: 'nominator',
            path: `pool/:address(${ADDRESS_REGEX.source})`,
            component: PagePool,
            props: true,
        }, {
            name: 'vesting',
            path: `vesting/:address(${ADDRESS_REGEX.source})`,
            component: PageVesting,
            props: true,
        }, {
            name: 'multisig',
            path: `multisig/:address(${ADDRESS_REGEX.source})`,
            component: PageMultisig,
            props: true,
        }, {
            name: 'multisig_order',
            path: `multisig-order/:address(${ADDRESS_REGEX.source})`,
            component: PageMultisigOrder,
            props: true,
        }, {
            name: 'locker',
            path: `locker/:address(${ADDRESS_REGEX.source})`,
            component: PageLocker,
            props: true,
        }, {
            name: 'single_nominator',
            path: `single-nominator/:address(${ADDRESS_REGEX.source})`,
            component: PageSingleNominator,
            props: true,
        }, {
            name: 'suspended',
            path: 'suspended',
            component: PageSuspended,
        }, {
            name: 'stats',
            path: 'stats',
            component: PageStats,
        }, {
            name: 'validators',
            path: 'validators',
            component: PageValidators,
        },
        {
            name: 'market',
            path: 'market',
            component: PageMarket,
        },
        {
            name: 'apps',
            path: 'apps',
            component: PageApps,
            props: true,
        }, {
            name: 'apps-category',
            path: 'apps/:category',
            component: PageApps,
            props: true,
            children: [{
                name: 'app',
                path: ':app',
                component: AppWrapper,
                props: true,
            }],
        }, {
            name: 'whales',
            path: 'whales',
            component: PageWhales,
        },
        {
            path: 'top',
            redirect: { name: 'whales', replace: true },
        },
        {
            path: 'richlist',
            redirect: { name: 'whales', replace: true },
        },
        {
            name: 'config',
            path: 'config',
            component: PageConfig,
        },
        {
            name: 'advertise',
            path: 'advertise',
            component: PageAdvertise,
        },
        {
            path: 'ads',
            redirect: { name: 'advertise', replace: true },
        },
        {
            path: `:address(${ADDRESS_REGEX.source})`,
            redirect: { name: 'address', replace: true },
        }, {
            path: `:domain(${TON_DOMAIN_REGEX.source})`,
            beforeEnter: async (to, from, next) => {
                try {
                    const domain = to.params.domain;

                    const address = await getAddressByDomain(domain);

                    if (address) {
                        next({ name: 'address', params: { address }, replace: true });
                    } else {
                        next({ name: '404' });
                    }
                } catch (err) {
                    next({ name: '404' });
                }
            },
        }, {
            path: `:domain(${TELEGRAM_USERNAME_REGEX.source})`,
            beforeEnter: async (to, from, next) => {
                try {
                    const domain = to.params.domain;

                    const address = await getAddressByDomain(domain);

                    if (address) {
                        next({ name: 'address', params: { address }, replace: true });
                    } else {
                        next({ name: '404' });
                    }
                } catch (err) {
                    next({ name: '404' });
                }
            },
        }, {
            name: '404',
            path: '*',
            component: PageError,
        },
        ],
    }],
});

/* eslint no-unused-vars: "off" */
router.beforeEach((to, from, next) => {
    // replace main locale prefix with domain root:
    if (to.params.lang === APP_MAIN_LOCALE) {
        next({
            ...to,
            params: {
                ...to.params,
                lang: undefined,
            },
        });
    } else {
        next();
    }
});

router.afterEach((to, from) => {
    Vue.nextTick(() => {
        const address = to.params.address;

        if (address) {
            document.title = `${address} · Tonscan`;
        }
    });
});

export default router;
